import axios from "axios";
import { enqueueSnackbar } from "notistack";
import { API_BASE_URL } from "@/config/env";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

export const uploadPublicFile = async (file: File, module: string): Promise<string> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("module", module);

  const uploadRes = await fetch(`${API_BASE_URL}/file-upload/upload`, {
    method: "POST",
    body: formData,
    headers: {
      Accept: "*/*",
    },
    credentials: "include",
  });

  if (!uploadRes.ok) {
    const errText = await uploadRes.text();
    throw new Error(`❌ Upload failed: ${errText}`);
  }

  const uploadJson = await uploadRes.json();
  if (!uploadJson?.data?.url) {
    throw new Error("❌ Unexpected response: 'data.url' not found");
  }

  const uploadedUrl = uploadJson.data.url;
  console.log("✅ Uploaded image URL:", uploadedUrl);
  return uploadedUrl;
};

export const uploadPrivateFile = async (file: File, module: string): Promise<string> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("module", module);

  const uploadRes = await fetch(`${API_BASE_URL}/file-upload/upload/private`, {
    method: "POST",
    body: formData,
    headers: {
      Accept: "*/*",
    },
    credentials: "include",
  });

  if (!uploadRes.ok) {
    const errText = await uploadRes.text();
    throw new Error(`❌ Upload failed: ${errText}`);
  }

  const uploadJson = await uploadRes.json();
  if (!uploadJson?.data?.url) {
    throw new Error("❌ Unexpected response: 'data.url' not found");
  }

  const uploadedUrl = uploadJson.data.url;
  console.log("✅ Uploaded image URL:", uploadedUrl);
  return uploadedUrl;
};

const getFilenameFromContentDisposition = (contentDisposition: string | null): string | null => {
  if (!contentDisposition) return null;

  const matches = /filename[^;=\n]=((['"]).?\2|[^;\n]*)/.exec(contentDisposition);
  if (matches != null && matches[1]) {
    return matches[1].replace(/['"]/g, "");
  }

  return null;
};

const saveFile = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadFile = async (id: string): Promise<void> => {
  try {
    const url = `${baseUrl}/taas-cards/download/${id}`;
    const response = await axios.post(
      url,
      {},
      {
        responseType: "blob",
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*",
        },
        withCredentials: true,
      }
    );

    const contentDisposition = response.headers["content-disposition"];
    const filename = getFilenameFromContentDisposition(contentDisposition) || "download";
    saveFile(response.data, filename);
    enqueueSnackbar("File downloaded successfully", {
      variant: "success",
    });
  } catch (error: any) {
    let errorMessage = "Error downloading file";

    if (error.response) {
      // Try to parse the error message from the response
      if (error.response.data instanceof Blob) {
        // If the response is a blob, try to read it as text
        const text = await error.response.data.text();
        try {
          const jsonError = JSON.parse(text);
          errorMessage = jsonError.message || errorMessage;
        } catch {
          errorMessage = text || errorMessage;
        }
      } else if (typeof error.response.data === "object") {
        errorMessage = error.response.data.message || errorMessage;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    enqueueSnackbar(errorMessage, {
      variant: "error",
    });
  }
};

export const downloadProductFile = async (id: number, productId: number): Promise<void> => {
  try {
    const url = `${baseUrl}/taas-cards/download/${id}/${productId}`;
    const response = await axios.post(
      url,
      {},
      {
        responseType: "blob",
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*",
        },
        withCredentials: true,
      }
    );

    const contentDisposition = response.headers["content-disposition"];
    const filename = getFilenameFromContentDisposition(contentDisposition) || "download";
    saveFile(response.data, filename);
    // enqueueSnackbar("Product downloaded successfully", {
    //   variant: "success",
    // });
  } catch (error: any) {
    let errorMessage = "Error downloading file";

    if (error.response) {
      // Try to parse the error message from the response
      if (error.response.data instanceof Blob) {
        // If the response is a blob, try to read it as text
        const text = await error.response.data.text();
        try {
          const jsonError = JSON.parse(text);
          errorMessage = jsonError.message || errorMessage;
        } catch {
          errorMessage = text || errorMessage;
        }
      } else if (typeof error.response.data === "object") {
        errorMessage = error.response.data.message || errorMessage;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    enqueueSnackbar(errorMessage, {
      variant: "error",
    });
  }
};
